import React, { useState, useEffect } from 'react'
import { Card, Typography, Spin, message, Layout, Menu } from 'antd'
import {
	SafetyOutlined,
	EnvironmentOutlined,
	LaptopOutlined,
	UserOutlined,
} from '@ant-design/icons'
import { httpGet, httpPut } from '../../common/http.service'
import LocationFraudSubTab from './LocationFraudSubTab'
import DeviceChangeTrackingSubTab from './DeviceChangeTrackingSubTab'
import SingleDeviceLoginSubTab from './SingleDeviceLoginSubTab'

const { Title, Paragraph } = Typography
const { Sider, Content } = Layout

interface FraudDetectionSettings {
	restrictMockLocation: boolean
	enableDeviceChangeTracking: boolean
	singleDeviceLogin: boolean
}

interface FraudDetectionTabProps {
	initialSubMenu?: string
	showInternalMenu?: boolean
}

const subMenuItems = [
	{
		key: 'location-fraud',
		icon: <EnvironmentOutlined />,
		label: 'Location Fraud',
	},
	{
		key: 'device-change-tracking',
		icon: <LaptopOutlined />,
		label: 'Device Change Tracking',
	},
	{
		key: 'single-device-login',
		icon: <UserOutlined />,
		label: 'Single Device Login',
	},
]

const FraudDetectionTab: React.FC<FraudDetectionTabProps> = ({
	initialSubMenu,
	showInternalMenu = false,
}) => {
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [settings, setSettings] = useState<FraudDetectionSettings>({
		restrictMockLocation: true,
		enableDeviceChangeTracking: false,
		singleDeviceLogin: false,
	})

	useEffect(() => {
		loadSettings()
	}, [])

	const loadSettings = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/admin/fraud-detection')
			if (response?.data) {
				setSettings({
					restrictMockLocation: Boolean(response.data.restrictMockLocation), // Ensure boolean type
					enableDeviceChangeTracking: Boolean(response.data.enableDeviceChangeTracking),
					singleDeviceLogin: Boolean(response.data.singleDeviceLogin),
				})
			}
		} catch (error) {
			console.error('Failed to load fraud detection settings:', error)
		} finally {
			setLoading(false)
		}
	}

	// State for sub-menu navigation
	const [selectedSubMenu, setSelectedSubMenu] = useState('location-fraud')

	useEffect(() => {
		if (
			initialSubMenu === 'location-fraud' ||
			initialSubMenu === 'device-change-tracking' ||
			initialSubMenu === 'single-device-login'
		) {
			setSelectedSubMenu(initialSubMenu)
		}
	}, [initialSubMenu])

	const handleToggle = async (field: keyof FraudDetectionSettings, value: boolean) => {
		const updated = { ...settings, [field]: value }
		setSettings(updated)
		try {
			setSaving(true)
			await httpPut('/api/v1/admin/fraud-detection', updated)
			message.success('Fraud detection settings saved successfully')
		} catch (error: any) {
			message.error(
				error?.response?.data?.message || 'Failed to save fraud detection settings',
			)
			setSettings(settings) // revert on error
		} finally {
			setSaving(false)
		}
	}

	const renderSubContent = () => {
		switch (selectedSubMenu) {
			case 'location-fraud':
				return (
					<LocationFraudSubTab
						restrictMockLocation={settings.restrictMockLocation}
						onToggle={(val) => handleToggle('restrictMockLocation', val)}
						saving={saving}
					/>
				)
			case 'device-change-tracking':
				return (
					<DeviceChangeTrackingSubTab
						enableDeviceChangeTracking={settings.enableDeviceChangeTracking}
						onToggle={(val) => handleToggle('enableDeviceChangeTracking', val)}
						saving={saving}
					/>
				)
			case 'single-device-login':
				return (
					<SingleDeviceLoginSubTab
						singleDeviceLogin={settings.singleDeviceLogin}
						onToggle={(val) => handleToggle('singleDeviceLogin', val)}
						saving={saving}
					/>
				)
			default:
				return null
		}
	}

	return (
		<Card
			title={
				<span>
					<SafetyOutlined style={{ marginRight: 8 }} />
					Fraud Detection
				</span>
			}
			bordered={false}
			style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
		>
			<Paragraph type='secondary' style={{ marginBottom: 24 }}>
				Configure fraud detection rules that apply during check-in and check-out. Changes
				take effect immediately on the mobile app.
			</Paragraph>

			<Spin spinning={loading}>
				<Layout style={{ background: 'none' }}>
					{showInternalMenu && (
						<Sider
							width={200}
							style={{
								background: 'none',
								borderRight: '1px solid #f0f0f0',
								paddingRight: 16,
							}}
						>
							<Menu
								mode='inline'
								selectedKeys={[selectedSubMenu]}
								onClick={({ key }) => setSelectedSubMenu(key)}
								items={subMenuItems}
								style={{ borderRight: 0 }}
							/>
						</Sider>
					)}
					<Content style={{ paddingLeft: showInternalMenu ? 24 : 0 }}>
						{renderSubContent()}
					</Content>
				</Layout>
			</Spin>
		</Card>
	)
}

export default FraudDetectionTab
